/**
 * ✅ FASE 10: Performance Monitoring Hook
 * Captura métricas de Web Vitals e reporta para análise
 */

import { useEffect, useRef } from 'react';
import { onCLS, onFCP, onLCP, onTTFB, onINP, Metric } from 'web-vitals';
import { performanceMonitorService } from '@/services/performanceMonitorService';

export interface PerformanceMetrics {
  FCP: number | null;  // First Contentful Paint
  LCP: number | null;  // Largest Contentful Paint
  CLS: number | null;  // Cumulative Layout Shift
  TTFB: number | null; // Time to First Byte
  INP: number | null;  // Interaction to Next Paint
}

export function usePerformanceMonitor() {
  const metricsRef = useRef<PerformanceMetrics>({
    FCP: null,
    LCP: null,
    CLS: null,
    TTFB: null,
    INP: null,
  });

  const reportedRef = useRef(new Set<string>());

  useEffect(() => {
    const handleMetric = (metric: Metric) => {
      const { name, value, rating } = metric;
      
      // Evitar reportar a mesma métrica múltiplas vezes
      if (reportedRef.current.has(name)) return;
      reportedRef.current.add(name);

      // Atualizar referência local
      metricsRef.current[name as keyof PerformanceMetrics] = value;

      // Reportar para serviço
      performanceMonitorService.recordMetric({
        name,
        value,
        rating,
        timestamp: Date.now(),
      });

      // Log em dev mode
      if (import.meta.env.DEV) {
        const emoji = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌';
        console.log(`${emoji} [Web Vitals] ${name}: ${Math.round(value)}ms (${rating})`);
      }
    };

    // Capturar todas as métricas de Web Vitals
    onCLS(handleMetric);
    onFCP(handleMetric);
    onLCP(handleMetric);
    onTTFB(handleMetric);
    onINP(handleMetric);

    // Capturar métricas de navegação
    if ('performance' in window && 'getEntriesByType' in performance) {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      if (navigationEntries.length > 0) {
        const nav = navigationEntries[0];
        performanceMonitorService.recordNavigation({
          domContentLoaded: nav.domContentLoadedEventEnd - nav.domContentLoadedEventStart,
          loadComplete: nav.loadEventEnd - nav.loadEventStart,
          transferSize: nav.transferSize,
          type: nav.type,
        });
      }
    }

    // Cleanup não necessário pois Web Vitals registra apenas uma vez
  }, []);

  return metricsRef.current;
}
